import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { uiConfig, crudConfig } from '../../../conf/securitySchemes';

// Components
import Form from '../../../../components/Form';
import FeedbackCard from '../../../../components/FeedbackCard';
import { Dialog } from '../../../../components/Dialog';
import { DialogHeader } from '../../../../components/DialogHeader';
import { DialogBody } from '../../../../components/DialogBody';
import { DialogFooter } from '../../../../components/DialogFooter';
import { Button } from '../../../../components/Button';
import { Spinner } from '../../../../components/Spinner';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import TabbedContainer from '../../../../components/TabbedContainer';
import { PermissionsTable } from '../permissionsTable';

// Hooks
import { useCreateScheme } from '../../api/hooks/useCreateScheme';
import { useUpdateScheme } from '../../api/hooks/useUpdateScheme';
import { useSecuritySchemeById } from '../../api/hooks/useSecuritySchemeById';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

export const AddEditScheme = ({
    formModel,
    selectedSchemeId,
    closeDialog,
    sessionToken,
    saveDisabled,
    onMount,
    isExistingDoc
}) => {
    const [schemeId, setSchemeId] = React.useState(selectedSchemeId);
    const isNew = React.useMemo(() => schemeId === 'new', [schemeId]);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            CloseDialog();
        }
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const mutationConfig = {
        crudConfig,
        sessionToken,
        onErrorCb: (e) => {
            setFatalError(e);
            setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const createScheme = useCreateScheme({
        ...mutationConfig,
        onSuccessCb: ({ data, context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            setSchemeId(data);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const updateScheme = useUpdateScheme({
        ...mutationConfig,
        onSuccessCb: ({ context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const schemeQuery = useSecuritySchemeById({
        sessionToken,
        schemeId
    });
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [schemeState, setSchemeState] = React.useState(null);

    React.useEffect(() => {
        if (!isNew) {
            if (schemeQuery?.isLoading) {
                setSchemeState({});
                return;
            }
            if (typeof schemeQuery?.data === 'object') {
                setSchemeState(schemeQuery?.data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setSchemeState({});
            }
            return;
        } else {
            setSchemeState(typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {});
        }
    }, [isNew, schemeId, schemeQuery?.isLoading, schemeQuery?.data]);

    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newScheme) => {
        return {
            ...docState,
            dtoState: newScheme ? 'NEW' : 'UPDATED'
        };
    };

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...schemeState,
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('This security scheme already exists in the system.');
            return;
        }
        if (isNew) {
            createScheme.mutate({
                newScheme: update,
                context: { closeOnSuccess: buttonClicked === 'save' }
            });
        } else {
            updateScheme.mutate({ update, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(() => createScheme.isLoading || updateScheme.isLoading, [
        createScheme,
        updateScheme
    ]);

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    return (
        <>
            <div style={{ height: `30rem` }}>
                <Dialog close={onClose} size="lg" visible id="internal-security-editor-addEditScheme">
                    <DialogHeader close={onClose}>
                        {isNew ? 'Add Security Scheme' : `Edit ${schemeState?.schemeName}`}
                    </DialogHeader>
                    <DialogBody>
                        {fatalError != null && (
                            <FeedbackCard
                                intent={'danger'}
                                feedback={{
                                    heading: `An error has occurred.`,
                                    message:
                                        typeof fatalError === 'string'
                                            ? fatalError
                                            : `Please wait a few moments before trying your request
again.`
                                }}
                                details={fatalError}
                            />
                        )}
                        {isNew ? 'Enter' : 'Adjust'} required information below.
                        <p style={{ margin: `1.33rem 0 0 0` }}></p>
                        <TabbedContainer
                            expandHeaderX
                            tabs={{
                                details: {
                                    label: 'Details',
                                    alwaysMounted: true,
                                    children: (
                                        <>
                                            {!isNew &&
                                            (schemeQuery?.isLoading ||
                                                schemeState === null ||
                                                Object.keys(schemeState).length === 0) ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <Form
                                                    formModel={formModel}
                                                    isNew={isNew}
                                                    data={schemeState}
                                                    errors={errors}
                                                    compose={compose}
                                                    onApplyFormState={onApplyFormState}
                                                    setEdited={setEdited}
                                                    fatalError={fatalError}
                                                    onMount={onMount}
                                                />
                                            )}
                                        </>
                                    )
                                },
                                items: {
                                    label: 'Permissions',
                                    disabled: isNew,
                                    children: (
                                        <>
                                            {schemeQuery?.isLoading ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <PermissionsTable
                                                    data={schemeState}
                                                    sessionToken={sessionToken}
                                                    onSave={(update) => {
                                                        setSchemeState({
                                                            ...schemeState,
                                                            ...update
                                                        });
                                                        setEdited(true);
                                                    }}
                                                />
                                            )}
                                        </>
                                    )
                                }
                            }}
                        />
                    </DialogBody>
                    <DialogFooter>
                        {isLoading && <div style={{ marginRight: '0.5rem' }}>Loading...</div>}
                        {!isLoading && wasSuccessful && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            text
                            appearance="primary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('save');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Save Scheme
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('apply');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Apply
                        </Button>
                        <Button text appearance="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEditScheme;

AddEditScheme.propTypes = {
    selectedSchemeId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    saveDisabled: PropTypes.bool
};
